import { Link, useLocation } from 'react-router';

import { useUnreadCount } from '@shared/app-shell/queries';
import { formatNumber } from '@shared/format';
import { BellIcon } from '@shared/ui/icons/nav';

import { chromeCopy } from './copy';

/**
 * The notification control, and the one route reached from the bar rather than
 * from the column.
 *
 * Three rules the pack fixes and this component keeps:
 *
 * - The indicator is a **count**, never a coloured dot — colour alone carries no
 *   information.
 * - The same count is written into the link's accessible name, so the visible
 *   badge is `aria-hidden` and the figure is never announced twice.
 * - At zero the badge **leaves the document entirely**. A badge reading `٠`
 *   announces work that does not exist.
 *
 * The handle carries `aria-current="page"` on `/inbox`, and it is the only
 * control that can: the inbox has no entry in the navigation column, so without
 * this the one route reached from the bar would be the one route whose location
 * is never announced.
 */
export function InboxHandle() {
  const { data } = useUnreadCount();
  const { pathname } = useLocation();
  const unread = data?.unread_count ?? 0;
  const unreadLabel = formatNumber(unread);

  return (
    <Link
      to="/inbox"
      aria-current={pathname === '/inbox' ? 'page' : undefined}
      aria-label={unread > 0 ? chromeCopy.inbox.label(unreadLabel) : chromeCopy.inbox.labelEmpty}
      className="hit-target relative flex items-center justify-center rounded-sm text-ink transition-colors duration-fast hover:bg-hover-bg hover:text-accent-text"
    >
      <BellIcon />
      {unread > 0 ? (
        /*
          `text-accent-ink`, not `text-bg`. `--accent-ink` is the token defined as
          the readable ink *for* `--accent` — white on the dark theme, the warm
          off-white on the light one — and it clears AA in both (4.79:1 and
          7.63:1). `--bg` is the page's own ground: sitting it on the accent
          painted a near-black numeral on red at 10px bold, which is the
          `color-contrast` violation axe reported on every route of the console.
        */
        <span
          aria-hidden="true"
          className="leading-none absolute grid min-w-[1.125rem] place-items-center rounded-pill bg-accent px-1 text-[0.625rem] font-bold text-accent-ink [block-size:1.125rem] [inset-block-start:0.125rem] [inset-inline-end:0.125rem]"
        >
          <bdi>{unreadLabel}</bdi>
        </span>
      ) : null}
    </Link>
  );
}
